import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useSdrProfile, useUpdateSdrProfile } from '@/hooks/useSdrProfile';
import { Button, Card, Input } from '@solid/ui';
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor';

export default function AiSdrSettings() {
  const { data: profile, isLoading } = useSdrProfile();
  const updateProfile = useUpdateSdrProfile();

  const [companyName, setCompanyName] = useState('');
  const [identityMarkdown, setIdentityMarkdown] = useState('');
  const [customInstructionsMarkdown, setCustomInstructionsMarkdown] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (profile) {
      setCompanyName(profile.companyName || '');
      setIdentityMarkdown(profile.identityMarkdown || '');
      setCustomInstructionsMarkdown(profile.customInstructionsMarkdown || '');
      setDirty(false);
    }
  }, [profile]);

  const handleSave = async () => {
    if (!profile) return;
    await updateProfile.mutateAsync({
      id: profile.id,
      data: { companyName, identityMarkdown, customInstructionsMarkdown },
    });
    setDirty(false);
  };

  if (isLoading) {
    return <div className="text-grey-500">Loading...</div>;
  }

  if (!profile) {
    return <div className="text-grey-500">No AI SDR profile found.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-heading-2 text-secondary">AI SDR Training</h2>
          <p className="text-body-sm text-grey-500 mt-1 max-w-2xl">
            Tell the AI agent who it represents. These settings, along with the Ideal Customer Profile, control how leads are evaluated.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!dirty} loading={updateProfile.isPending}>
          {updateProfile.isPending ? (
            'Saving...'
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Card padding="sm">
        <Input
          label="Company Name"
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value);
            setDirty(true);
          }}
          placeholder="e.g., Solid Digital"
          hint="The name the AI uses to represent your company in conversation."
        />
      </Card>

      <Card padding="sm">
        <label className="block text-sm font-medium text-grey-700 mb-2">Agent Identity</label>
        <RichMarkdownEditor
          value={identityMarkdown}
          onChange={(md) => {
            setIdentityMarkdown(md);
            setDirty(true);
          }}
          placeholder="Describe your company, what you do, what you sell, and the tone the AI should use..."
          minHeight="20rem"
        />
        <p className="text-xs text-grey-400 mt-2">
          Describe your company, your services or products, and the tone the AI should use. This is delivered to the AI verbatim.
        </p>
      </Card>

      <Card padding="sm">
        <label className="block text-sm font-medium text-grey-700 mb-2">Custom Instructions</label>
        <RichMarkdownEditor
          value={customInstructionsMarkdown}
          onChange={(md) => {
            setCustomInstructionsMarkdown(md);
            setDirty(true);
          }}
          placeholder="Add rules or guidelines that override or supplement the default evaluation behavior..."
          minHeight="16rem"
        />
        <p className="text-xs text-grey-400 mt-2">
          Use this to override default behavior, e.g. "We also serve nonprofits — never flag them as red flags."
        </p>
      </Card>
    </div>
  );
}
